import type { ReactNode } from 'react'
import { Platform, Pressable, Text, View } from 'react-native'

import type { IconName } from '../../../components/ui/Icon'
import { ChevronDownIcon } from '../../../components/ui/ChevronDownIcon'
import { ChevronRightIcon } from '../../../components/ui/ChevronRightIcon'
import { Icon, symbolName } from '../../../components/ui/Icon'
import { Spinner } from '../../../components/ui/Spinner'
import { colors } from '../../../theme'
import { body3, label1 } from '../../../theme/typography'

export interface SettingsRowProps {
  icon?: IconName
  leadingIcon?: ReactNode
  accessoryIcon?: ReactNode
  label: string
  subtitle?: string
  value?: string
  onPress: () => void
  isLoading?: boolean
  isDestructive?: boolean
  isLast?: boolean
  compact?: boolean
  accessory?: 'chevron' | 'external' | 'dropdown'
  labelColor?: string
}

const cardShadowStyle =
  Platform.OS === 'web'
    ? {
        boxShadow: '0px 2px 5px 0px #0000000D',
      }
    : {
        shadowColor: '#000000',
        shadowOpacity: 0.05,
        shadowRadius: 5,
        shadowOffset: { width: 0, height: 2 },
        elevation: 2,
      }

const settingsRowClassName = 'flex-row items-center px-4 active:bg-fill'
const settingsRowDefaultHeightClassName = 'h-16.5'
const settingsRowCompactHeightClassName = 'h-12.5'
const settingsRowIconClassName = 'mr-3 w-6 items-center justify-center'

export const settingsButtonHeightClassName = 'h-10.5'

const settingsRowLabelTextStyle = {
  letterSpacing: 0,
  lineHeight: 30,
} as const

const settingsRowSubtitleTextStyle = {
  color: colors.textBody,
  fontSize: 13,
  fontWeight: '400',
  letterSpacing: 0,
  lineHeight: 18,
  marginTop: -3,
} as const

const settingsRowValueTextStyle = {
  color: colors.textMuted,
  marginRight: 8,
  fontWeight: '400',
  letterSpacing: 0,
  lineHeight: 19,
} as const

export const settingsSectionTitleTextStyle = {
  color: colors.textBody,
  letterSpacing: 0,
  lineHeight: 26,
} as const

export const settingsEmailBodyTextStyle = {
  color: colors.textBody,
  marginTop: 2,
  fontSize: 14,
  fontWeight: 'normal',
  lineHeight: 19,
  letterSpacing: 0,
} as const

export const settingsFooterVersionTextStyle = {
  color: colors.textBody,
  textAlign: 'center',
  marginBottom: 8,
  fontWeight: 'normal',
  lineHeight: 19,
  letterSpacing: 0,
} as const

export function SettingsCard({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <View
      className={`overflow-hidden rounded-lg bg-background-secondary ${className}`}
      style={cardShadowStyle}
    >
      {children}
    </View>
  )
}

export function SettingsRow({
  icon,
  leadingIcon,
  accessoryIcon: customAccessoryIcon,
  label,
  subtitle,
  value,
  onPress,
  isLoading,
  isDestructive,
  isLast,
  compact,
  accessory = 'chevron',
  labelColor,
}: SettingsRowProps) {
  const iconColor = isDestructive ? colors.destructive : colors.inkSecondary
  const resolvedLabelColor =
    labelColor ?? (isDestructive ? colors.destructive : colors.textHeader)
  const accessoryIcon =
    accessory === 'external' ? (
      <Icon
        name={symbolName('arrow.up.right.square', 'open_in_new')}
        size={20}
        color={colors.inkSecondary}
      />
    ) : accessory === 'dropdown' ? (
      <ChevronDownIcon color={colors.inkSecondary} />
    ) : (
      <ChevronRightIcon color={colors.inkSecondary} />
    )

  return (
    <Pressable
      className={`${settingsRowClassName} ${compact ? settingsRowCompactHeightClassName : settingsRowDefaultHeightClassName} ${!isLast ? 'border-b border-border/80' : ''}`}
      onPress={onPress}
      disabled={isLoading}
    >
      <View className={settingsRowIconClassName}>
        {leadingIcon ?? <Icon name={icon!} size={24} color={iconColor} />}
      </View>
      <View className={`flex-1 ${subtitle ? 'justify-center' : ''}`}>
        <Text
          style={[
            label1,
            { color: resolvedLabelColor },
            settingsRowLabelTextStyle,
          ]}
        >
          {label}
        </Text>
        {subtitle ? (
          <Text style={[body3, settingsRowSubtitleTextStyle]}>{subtitle}</Text>
        ) : null}
      </View>
      {isLoading ? (
        <Spinner color={colors.inkSecondary} />
      ) : (
        <View className="flex-row items-center">
          {value ? (
            <Text style={[body3, settingsRowValueTextStyle]}>{value}</Text>
          ) : null}
          {customAccessoryIcon ?? accessoryIcon}
        </View>
      )}
    </Pressable>
  )
}
